<template>
  <div class="py-6">
    <form @submit.prevent ref="form" class="space-y-6">
      <div class="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
        <h2 class="text-lg font-semibold text-gray-800">Édition du rôle</h2>

        <!-- Nom du rôle -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Nom du rôle
          </label>
          <input
            required
            type="text"
            v-model="role.name"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-800 shadow-theme-xs"
            placeholder="Ex: Administrateur"
          />
        </div>

        <!-- Permissions -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700">
            Permissions
          </label>

          <!-- Filtre par catégorie -->
          <div class="mb-4">
            <select
              v-model="selectedCategory"
              class="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-800 shadow-theme-xs"
            >
              <option value="">Toutes les catégories</option>
              <option
                v-for="(category, key) in displayableCategories"
                :key="key"
                :value="key"
              >
                {{ category.label }}
              </option>
            </select>
          </div>

          <!-- Permissions groupées par catégorie -->
          <div class="space-y-4">
            <div
              v-for="(category, categoryKey) in filteredCategories"
              :key="categoryKey"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-center gap-2 mb-3">
                <i
                  :class="category.icon"
                  :style="{ color: category.color }"
                ></i>
                <h4 class="font-medium text-gray-800">{{ category.label }}</h4>
                <span class="text-xs text-gray-500"
                  >({{
                    getPermissionsInCategory(categoryKey).length
                  }}
                  permissions)</span
                >
              </div>

              <div class="flex flex-wrap gap-2">
                <label
                  v-for="(permission, permKey) in getPermissionsInCategory(
                    categoryKey,
                  )"
                  :key="permKey"
                  class="flex items-center gap-2 text-sm bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg cursor-pointer border border-gray-200"
                  :title="permission.description"
                >
                  <input
                    type="checkbox"
                    :value="permKey"
                    v-model="role.permissions"
                    class="rounded"
                  />
                  <i :class="permission.icon" class="text-xs"></i>
                  <span>{{ permission.label }}</span>
                  <span
                    v-if="permission.level"
                    class="text-xs px-1.5 py-0.5 rounded"
                    :style="{
                      backgroundColor:
                        displayableLevels[permission.level]?.color + '20',
                      color: displayableLevels[permission.level]?.color,
                    }"
                  >
                    {{ displayableLevels[permission.level]?.label }}
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!-- Sélection rapide -->
          <div class="mt-4 pt-4 border-t border-gray-200">
            <div class="flex gap-2">
              <button
                type="button"
                @click="selectAllPermissions"
                class="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Tout sélectionner
              </button>
              <button
                type="button"
                @click="deselectAllPermissions"
                class="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Tout désélectionner
              </button>
              <button
                type="button"
                @click="selectByLevel('basic')"
                class="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                Basique uniquement
              </button>
              <button
                type="button"
                @click="selectByLevel('standard')"
                class="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Standard +
              </button>
              <button
                type="button"
                @click="selectByLevel('admin')"
                class="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Admin complet
              </button>
            </div>
          </div>
        </div>

        <!-- Dernière mise à jour -->
        <div v-if="role.updatedAt">
          <label class="text-sm font-medium text-gray-700 block">
            Dernière mise à jour
          </label>
          <div class="text-sm text-gray-500">
            {{ new Date(role.updatedAt).toLocaleString() }}
          </div>
        </div>
      </div>

      <button class="hidden"></button>
    </form>

    <!-- Bouton sauvegarder -->
    <div class="flex justify-end !mt-12">
      <button type="button" @click="startSave" class="buttonComponent w-full">
        Enregistrer le rôle
      </button>
    </div>

    <!-- ActionTrigger -->
    <ActionTrigger
      dataKey="saveRole"
      ref="saveRole"
      @success="() => $emit('saved')"
      @failed="
        err => {
          Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
            title: 'Échec d\'enregistrement',
            body: err.result?.message || 'Erreur inconnue',
          })
        }
      "
    />
  </div>
</template>

<script setup>
import { reactive, ref, watch, computed } from 'vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import displayablePermissions from '@/project/configs/displayablePermissions.json'

const props = defineProps({
  roleData: { type: Object, default: () => ({}) },
})

const saveRole = ref(null)
const form = ref(null)
const selectedCategory = ref('')

const role = reactive({
  id: null,
  name: '',
  permissions: [],
  updatedAt: null,
})

// Extraire les données displayables
const displayableCategories = computed(() => displayablePermissions.categories)
const displayableLevels = computed(() => displayablePermissions.levels)
const allPermissions = computed(() => displayablePermissions.permissions)

// Filtrer les catégories selon la sélection
const filteredCategories = computed(() => {
  if (!selectedCategory.value) {
    return displayableCategories.value
  }

  const filtered = {}
  filtered[selectedCategory.value] =
    displayableCategories.value[selectedCategory.value]
  return filtered
})

// Obtenir les permissions pour une catégorie donnée
function getPermissionsInCategory(categoryKey) {
  const permissions = {}
  Object.entries(allPermissions.value).forEach(([key, permission]) => {
    if (permission.category === categoryKey) {
      permissions[key] = permission
    }
  })
  return permissions
}

// Fonctions de sélection rapide
function selectAllPermissions() {
  role.permissions = Object.keys(allPermissions.value)
}

function deselectAllPermissions() {
  role.permissions = []
}

function selectByLevel(targetLevel) {
  const permissions = []

  if (targetLevel === 'basic') {
    Object.entries(allPermissions.value).forEach(([key, permission]) => {
      if (permission.level === 'basic') {
        permissions.push(key)
      }
    })
  } else if (targetLevel === 'standard') {
    Object.entries(allPermissions.value).forEach(([key, permission]) => {
      if (permission.level === 'basic' || permission.level === 'standard') {
        permissions.push(key)
      }
    })
  } else if (targetLevel === 'admin') {
    permissions.push(...Object.keys(allPermissions.value))
  }

  role.permissions = permissions
}

watch(
  () => props.roleData,
  val => {
    if (val && Object.keys(val).length) {
      Object.assign(role, val)
    }
  },
  { immediate: true },
)

function startSave() {
  const isValid = form.value.checkValidity()
  if (!isValid) {
    form.value.reportValidity()
    return
  }
  saveItem()
}

function saveItem() {
  saveRole.value.trigger({
    id: role.id,
    name: role.name,
    permissions: role.permissions,
    updatedAt: new Date().toISOString(),
  })
}
</script>
